/**
 * Render-isolated selectors over `messages.byChannelComposite`.
 *
 * Phase 2 architecture: ONE shared `zero.materialize(byChannelComposite)`
 * view per (channelId, limit) cache key, and four `useZeroSelector`
 * subscriptions on top of it — one for the message list, three for the
 * child-table Maps (reactions, attachments, link_previews).
 *
 * `useZeroSelector` runs each selector on every IVM push but only fires
 * a React update when that selector's output materially changes (via
 * `isEqual`). So:
 *   - reactions update → only the reactions-Map consumer re-renders
 *   - attachments update → only the attachments-Map consumer re-renders
 *   - new message arrives → only the messages-list consumer re-renders
 *
 * One server subscription. Four React-update boundaries. Same render
 * isolation guarantees as the pre-Phase-2 per-table query design (PR
 * 5.1, commit `e2365bec`; render-cascade-seal, commit `3f459a3b`),
 * preserved on top of the composite shape.
 *
 * Per-(channelId, limit) selector closures are kept in module-level Maps
 * so consumers of the same channel share identity-preserving state. When
 * a channel is no longer subscribed (view destroyed by the ViewCache
 * 5s grace), its closure is also reaped — see `releaseSelectorsFor`.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { useQueryDebugLog } from "../../hooks/use-debug-query.js";
import { useZeroSelector } from "../../hooks/use-zero-selector.js";
import { queries } from "../queries";
import { createIncrementalGroupSelector } from "../incremental-map-selector";

const COMPOSITE_TTL = "5m";

function buildCacheKey(channelId: string, limit: number): string {
  return `messages.byChannelComposite:${channelId}:${limit}`;
}

type Status = {
  type: "unknown" | "complete" | "error";
  retry?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
};

// ─── Child-array Map selectors ─────────────────────────────────────
//
// Flatten the composite's nested `.reactions[]` / `.attachments[]` /
// `.link_previews[]` arrays across all messages, then group by
// `message_id` with content-aware identity preservation. Same equality
// predicates the deleted `message-children.ts` used (verified against
// commit history of that file).

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Reaction = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CommunitySignal = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Attachment = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LinkPreview = any;

function createReactionsSelector() {
  const grouper = createIncrementalGroupSelector<Reaction, string, Reaction>(
    (r) => r.message_id as string,
    (r) => r,
    (prev, next) => prev.id === next.id && prev.emoji === next.emoji,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data: readonly any[]): Map<string, Reaction[]> => {
    const flat: Reaction[] = [];
    for (const msg of data) {
      if (Array.isArray(msg.reactions)) {
        for (const r of msg.reactions) flat.push(r);
      }
    }
    return grouper(flat);
  };
}

function createCommunitySignalsSelector() {
  const grouper = createIncrementalGroupSelector<
    CommunitySignal,
    string,
    CommunitySignal
  >(
    (s) => s.message_id as string,
    (s) => s,
    (prev, next) =>
      prev.id === next.id && prev.signal_type === next.signal_type,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data: readonly any[]): Map<string, CommunitySignal[]> => {
    const flat: CommunitySignal[] = [];
    for (const msg of data) {
      if (Array.isArray(msg.community_signals)) {
        for (const s of msg.community_signals) flat.push(s);
      }
    }
    return grouper(flat);
  };
}

function createAttachmentsSelector() {
  const grouper = createIncrementalGroupSelector<
    Attachment,
    string,
    Attachment
  >(
    (a) => a.message_id as string,
    (a) => a,
    (prev, next) =>
      prev.id === next.id &&
      prev.storage_url === next.storage_url &&
      prev.thumbnail_url === next.thumbnail_url,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data: readonly any[]): Map<string, Attachment[]> => {
    const flat: Attachment[] = [];
    for (const msg of data) {
      if (Array.isArray(msg.attachments)) {
        for (const a of msg.attachments) flat.push(a);
      }
    }
    return grouper(flat);
  };
}

function createLinkPreviewsSelector() {
  const grouper = createIncrementalGroupSelector<
    LinkPreview,
    string,
    LinkPreview
  >(
    (lp) => lp.message_id as string,
    (lp) => lp,
    (prev, next) =>
      prev.id === next.id &&
      prev.title === next.title &&
      prev.description === next.description &&
      prev.image_url === next.image_url,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data: readonly any[]): Map<string, LinkPreview[]> => {
    const flat: LinkPreview[] = [];
    for (const msg of data) {
      if (Array.isArray(msg.link_previews)) {
        for (const lp of msg.link_previews) flat.push(lp);
      }
    }
    return grouper(flat);
  };
}

// ─── Per-(channelId, limit) selector caches ────────────────────────
//
// Each consumer hook gets its OWN selector closure per cache key, but
// closures are shared across all consumers of the same key. This keeps
// per-row / per-group identity preservation consistent across multiple
// components reading the same channel (e.g., MessageArea + MessageList
// + ThreadPanel future composite reads).
//
// Closures live as long as the underlying view is kept alive in the
// ViewCache. `releaseSelectorsFor(cacheKey)` is fired by a paired
// `useEffect` cleanup — when the last subscriber leaves, the selector
// closure is freed alongside the view.

interface ChannelSelectorBundle {
  readonly reactions: ReturnType<typeof createReactionsSelector>;
  readonly communitySignals: ReturnType<typeof createCommunitySignalsSelector>;
  readonly attachments: ReturnType<typeof createAttachmentsSelector>;
  readonly linkPreviews: ReturnType<typeof createLinkPreviewsSelector>;
  refCount: number;
}

const selectorBundles = new Map<string, ChannelSelectorBundle>();

function acquireSelectors(cacheKey: string): ChannelSelectorBundle {
  let bundle = selectorBundles.get(cacheKey);
  if (!bundle) {
    bundle = {
      reactions: createReactionsSelector(),
      communitySignals: createCommunitySignalsSelector(),
      attachments: createAttachmentsSelector(),
      linkPreviews: createLinkPreviewsSelector(),
      refCount: 0,
    };
    selectorBundles.set(cacheKey, bundle);
  }
  bundle.refCount++;
  return bundle;
}

function releaseSelectors(cacheKey: string): void {
  const bundle = selectorBundles.get(cacheKey);
  if (!bundle) return;
  bundle.refCount--;
  if (bundle.refCount <= 0) {
    selectorBundles.delete(cacheKey);
  }
}

// ─── Stable defaults ───────────────────────────────────────────────

const EMPTY_REACTIONS_MAP: Map<string, Reaction[]> = new Map();
const EMPTY_SIGNALS_MAP: Map<string, CommunitySignal[]> = new Map();
const EMPTY_ATTACHMENTS_MAP: Map<string, Attachment[]> = new Map();
const EMPTY_LINK_PREVIEWS_MAP: Map<string, LinkPreview[]> = new Map();

// ─── Internal: useChannelSelectorBundle ────────────────────────────
//
// Acquires the per-(channelId, limit) selector bundle on mount and
// releases it on unmount. Returns the bundle (or null if channelId is
// null). Refcount-paired with `acquireSelectors` / `releaseSelectors`.

function useChannelSelectorBundle(
  cacheKey: string | null,
): ChannelSelectorBundle | null {
  // Acquire SYNCHRONOUSLY via useState lazy init so the very first render
  // already has the real selector. Deferring acquisition to useEffect (an
  // earlier draft) had a fatal flaw: by the time the effect ran and
  // `setBundle` updated the selector, `useZeroSelector`'s listener had
  // already fired ONCE with the fallback `() => EMPTY` selector and
  // recorded an EMPTY result. For any quiet channel (no further pushes),
  // the listener never re-fired — multiplexed channels stayed blank
  // until they received a new push. useState lazy init is React's
  // documented way to do init-time work safely (one call per instance,
  // even under StrictMode).
  //
  // Bundle + acquiredKey are captured together in the SAME useState call
  // so they can never drift. If `cacheKey` changes mid-instance (e.g.
  // a limit-bump shifts the key from `:300` to `:600`), the bundle stays
  // pinned to its original key and the unmount cleanup releases what
  // was actually acquired — no refcount leak. Selectors are key-agnostic
  // (they only mutate their own internal `prevMap`), so processing
  // limit-600 data through the limit-300 closure remains correct; the
  // ViewCache is the layer that re-shares the materialization at the new
  // key.
  const [acquired] = useState<{
    bundle: ChannelSelectorBundle | null;
    key: string | null;
  }>(() => ({
    bundle: cacheKey ? acquireSelectors(cacheKey) : null,
    key: cacheKey,
  }));

  useEffect(() => {
    return () => {
      if (acquired.key !== null) releaseSelectors(acquired.key);
    };
    // `acquired` is immutable for the lifetime of this hook instance
    // (set once by useState lazy init). Empty deps are correct here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return acquired.bundle;
}

// ─── Public hooks ───────────────────────────────────────────────────

/**
 * Subscribe to the channel's message list. Thin `useQuery` wrapper around
 * `byChannelComposite`.
 *
 * Why `useQuery` and NOT `useZeroSelector` (which the three child-Map
 * hooks below DO use): MessageArea has consumers that depend on the
 * `unknown → complete` status transition firing a React re-render —
 * the loading-skeleton gate (`hasCompletedOnceRef.current = true` in
 * the render body) and the reconnect-snapshot cleanup (`useEffect`
 * with `channelMessagesStatus.type` in deps). `useZeroSelector`
 * intentionally suppresses status-only emissions to prevent cold-
 * startup render storms (see CLAUDE.md "Render selectors" guidance).
 * For empty channels — where data goes from `[]` to `[]` while status
 * flips — the suppression keeps the skeleton up indefinitely. `useQuery`
 * has no such suppression and reliably notifies on every IVM delivery.
 *
 * The render-isolation goal is preserved: the row-level memoization
 * via the three child-Map hooks below is what prevents a 600-row
 * cascade. MessageArea + MessageListView running their function
 * bodies once per push is cheap; what matters is that `MessageRow.memo`
 * holds for unchanged rows, which it does because the children come
 * from stable Maps.
 *
 * Server-side: `useQuery` and `useZeroSelector` for the same query
 * identity share the IVM pipeline (one server sub) — they only
 * differ in the client-side TypedView caching layer.
 */
export function useChannelMessages(channelId: string | null, limit: number) {
  const result = useQuery(
    channelId
      ? queries.messages.byChannelComposite({ channelId, limit })
      : null,
  );
  useQueryDebugLog("messages.byChannelComposite", result);
  return result;
}

/**
 * Subscribe to the channel's per-message reactions Map. React only
 * re-renders when reactions actually change — NOT on message edits or
 * attachments / link-preview updates.
 */
export function useChannelReactionsMap(
  channelId: string | null,
  limit: number,
): [Map<string, Reaction[]>, Status] {
  const cacheKey = channelId ? buildCacheKey(channelId, limit) : null;
  const bundle = useChannelSelectorBundle(cacheKey);

  const selector = useMemo(() => {
    if (!bundle) return () => EMPTY_REACTIONS_MAP;
    return bundle.reactions;
  }, [bundle]);

  return useZeroSelector(
    cacheKey ?? "channel-reactions:none",
    cacheKey
      ? queries.messages.byChannelComposite({
          channelId: channelId as string,
          limit,
        })
      : null,
    selector,
    {
      ttl: COMPOSITE_TTL,
      defaultValue: EMPTY_REACTIONS_MAP,
    },
  );
}

/**
 * Subscribe to the channel's per-message community-signals Map (helpful /
 * worth_reading / best_answer). React only re-renders when signals change —
 * NOT on message edits, reactions, attachments, or link-preview updates.
 * Same one-server-sub + row-level isolation guarantees as the reactions Map.
 */
export function useChannelCommunitySignalsMap(
  channelId: string | null,
  limit: number,
): [Map<string, CommunitySignal[]>, Status] {
  const cacheKey = channelId ? buildCacheKey(channelId, limit) : null;
  const bundle = useChannelSelectorBundle(cacheKey);

  const selector = useMemo(() => {
    if (!bundle) return () => EMPTY_SIGNALS_MAP;
    return bundle.communitySignals;
  }, [bundle]);

  return useZeroSelector(
    cacheKey ?? "channel-community-signals:none",
    cacheKey
      ? queries.messages.byChannelComposite({
          channelId: channelId as string,
          limit,
        })
      : null,
    selector,
    {
      ttl: COMPOSITE_TTL,
      defaultValue: EMPTY_SIGNALS_MAP,
    },
  );
}

/**
 * Subscribe to the channel's per-message attachments Map. React only
 * re-renders when attachments change.
 */
export function useChannelAttachmentsMap(
  channelId: string | null,
  limit: number,
): [Map<string, Attachment[]>, Status] {
  const cacheKey = channelId ? buildCacheKey(channelId, limit) : null;
  const bundle = useChannelSelectorBundle(cacheKey);

  const selector = useMemo(() => {
    if (!bundle) return () => EMPTY_ATTACHMENTS_MAP;
    return bundle.attachments;
  }, [bundle]);

  return useZeroSelector(
    cacheKey ?? "channel-attachments:none",
    cacheKey
      ? queries.messages.byChannelComposite({
          channelId: channelId as string,
          limit,
        })
      : null,
    selector,
    {
      ttl: COMPOSITE_TTL,
      defaultValue: EMPTY_ATTACHMENTS_MAP,
    },
  );
}

/**
 * Subscribe to the channel's per-message link previews Map. React only
 * re-renders when link-preview metadata changes.
 */
export function useChannelLinkPreviewsMap(
  channelId: string | null,
  limit: number,
): [Map<string, LinkPreview[]>, Status] {
  const cacheKey = channelId ? buildCacheKey(channelId, limit) : null;
  const bundle = useChannelSelectorBundle(cacheKey);

  const selector = useMemo(() => {
    if (!bundle) return () => EMPTY_LINK_PREVIEWS_MAP;
    return bundle.linkPreviews;
  }, [bundle]);

  return useZeroSelector(
    cacheKey ?? "channel-link-previews:none",
    cacheKey
      ? queries.messages.byChannelComposite({
          channelId: channelId as string,
          limit,
        })
      : null,
    selector,
    {
      ttl: COMPOSITE_TTL,
      defaultValue: EMPTY_LINK_PREVIEWS_MAP,
    },
  );
}

// ─── Test-only helpers ─────────────────────────────────────────────

export const __test = {
  buildCacheKey,
  createReactionsSelector,
  createAttachmentsSelector,
  createLinkPreviewsSelector,
  selectorBundles,
};
